import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import type { Pending } from '../components/AlertDialog';
import type { ConfirmRequest } from './stack';
import { useLayerStack } from './useLayer';

const ConfirmHost = lazy(() => import('../components/AlertDialog').then((m) => ({ default: m.ConfirmHost })));

/**
 * Receives the stack's confirm requests (`useConfirm`, discard guards without their own bar) and
 * mounts the confirm dialog on the first request, so it costs nothing on first paint.
 */
export function ConfirmBridge() {
  const stack = useLayerStack();
  const pending = useRef<Pending[]>([]);
  const [wanted, setWanted] = useState(false);
  useEffect(() => {
    if (wanted) return;
    stack.setConfirmHost(
      (request) =>
        new Promise<boolean>((resolve) => {
          pending.current.push({ ...request, resolve });
          setWanted(true);
        }),
    );
  }, [stack, wanted]);
  useEffect(() => () => stack.setConfirmHost(null), [stack]);
  return wanted ? (
    <Suspense fallback={null}>
      <ConfirmHost pending={pending.current} />
    </Suspense>
  ) : null;
}

/**
 * Promise-based confirmation for irreversible actions: `if (await confirm({ title, body,
 * confirmLabel, danger, typeToConfirm })) …`. Requires LayerRoot (resolves false without it).
 * Prefer Undo for reversible actions (plan principle 4).
 */
export function useConfirm(): (request: ConfirmRequest) => Promise<boolean> {
  const stack = useLayerStack();
  return useCallback((request: ConfirmRequest) => stack.confirm(request), [stack]);
}
